import React from "react";
import styled, { keyframes } from "styled-components";
import contactusvideo from "../../../assets/images/contactUs/contactus.mp4";

// === Animations ===
const floatInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// === Layout ===
const Section = styled.section`
  position: relative;
  width: 100%;
  min-height: 100vh;
  padding: 120px 24px 80px; /* space for navbar at top */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: radial-gradient(circle at top left, #00c6ff 0, #062c3f 40%, #020617 90%);
  font-family: "Poppins", system-ui;

  &::before {
    content: "";
    position: absolute;
    inset: -40%;
    background: radial-gradient(circle at 15% 10%, rgba(56, 189, 248, 0.2), transparent 55%),
      radial-gradient(circle at 85% 90%, rgba(94, 234, 212, 0.14), transparent 55%);
    opacity: 0.9;
    pointer-events: none;
  }
`;

const GlowOrb = styled.div`
  position: absolute;
  width: 420px;
  height: 420px;
  border-radius: 999px;
  background: radial-gradient(circle at 30% 20%, #ffffff, rgba(15, 23, 42, 0.25));
  top: -120px;
  right: -80px;
  opacity: 0.18;
  filter: blur(2px);
  pointer-events: none;
`;

const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 1160px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  z-index: 1;
  animation: ${floatInUp} 0.6s ease-out both;

  @media (min-width: 820px) {
    flex-direction: row;
    align-items: center;
    gap: 40px;
  }
`;

// === Left content ===
const Content = styled.div`
  flex: 1.1;
  color: #e5f2ff;
  text-align: left;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(56, 189, 248, 0.5);
  color: #7dd3fc;
`;

const Title = styled.h1`
  margin: 18px 0 10px;
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.3;

  span {
    color: #22d3ee;
  }

  @media (min-width: 768px) {
    font-size: 3rem;
  }

  @media (min-width: 1024px) {
    font-size: 3.4rem;
  }
`;

const AccentBar = styled.div`
  width: 160px;
  height: 3px;
  border-radius: 999px;
  margin: 14px 0 18px;
  background: linear-gradient(90deg, #22c1c3, #3b82f6, #8b5cf6);
`;

const Description = styled.p`
  font-size: 0.98rem;
  line-height: 1.9;
  color: #cbd5f5;
  max-width: 520px;
  text-align: justify;

  @media (min-width: 768px) {
    font-size: 1.03rem;
  }
`;

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-top: 26px;
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 22px;
  border-radius: 999px;
  font-size: 0.96rem;
  font-weight: 600;
  text-decoration: none;
  color: #0b1220;
  background: #f9fafb;
  border: none;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.8);
  transition: background 0.3s, transform 0.15s ease, box-shadow 0.25s ease;

  i {
    font-size: 0.9rem;
  }

  &:hover {
    background: linear-gradient(90deg, #22c1c3, #3b82f6);
    color: #f9fafb;
    transform: translateY(-2px);
    box-shadow: 0 24px 60px rgba(15, 23, 42, 1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.9);
  }
`;

const SubNote = styled.span`
  font-size: 0.8rem;
  color: #9ca3af;
`;

// === Right video ===
const VideoContainer = styled.div`
  flex: 0.9;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VideoShell = styled.div`
  width: 100%;
  max-width: 520px;
  border-radius: 26px;
  padding: 10px;
  background:
    linear-gradient(135deg, rgba(34, 193, 195, 0.24), rgba(59, 130, 246, 0.14))
      border-box;
  box-shadow: 0 30px 90px rgba(15, 23, 42, 1);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(148, 163, 184, 0.7);
`;

const VideoInner = styled.div`
  border-radius: 20px;
  overflow: hidden;
  position: relative;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const VideoOverlayTag = styled.div`
  position: absolute;
  bottom: 14px;
  left: 14px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  background: rgba(15, 23, 42, 0.75);
  color: #e5e7eb;
  border: 1px solid rgba(148, 163, 184, 0.8);
`;

// === Component ===
const ContactUsHeader = () => {
  return (
    <Section>
      <GlowOrb />

      <Container>
        <Content>
          <Badge>Contact • NexTech PLM</Badge>
          <Title>
            Unleash your thoughts & ideas. <span>We&apos;re all ears.</span>
          </Title>
          <AccentBar />
          <Description>
            Your thoughts matter to us. Whether you have questions, feedback, or
            are interested in collaboration opportunities, we want to hear from you.
            No question is too big, and no idea is too ambitious. Reach out today,
            and let’s explore how we can connect.
          </Description>

          <CTAGroup>
            <Button href="#contactusform">
              Get In Touch <i className="fa-solid fa-arrow-right" />
            </Button>
            <SubNote>Response time: typically within 1 business day.</SubNote>
          </CTAGroup>
        </Content>

        <VideoContainer>
          <VideoShell>
            <VideoInner>
              <Video autoPlay muted loop playsInline>
                <source src={contactusvideo} type="video/mp4" />
                Your browser does not support the video tag.
              </Video>
              <VideoOverlayTag>Inside NexTech • PLM in action</VideoOverlayTag>
            </VideoInner>
          </VideoShell>
        </VideoContainer>
      </Container>
    </Section>
  );
};

export default ContactUsHeader;
